import {useQuery} from "@apollo/client";
import classnames from "classnames";
import {formatISO} from "date-fns";
import {plural} from "pluralize";
import {FaPhone, FaRegEnvelope} from "react-icons/fa6";
import {ImPrinter} from "react-icons/im";

import logo from "@/assets/images/default.svg";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {dateOnly, humanFriendlyInt, linebreaksbr, shortCurrency} from "@/lib/format.tsx";
import {
  CalculatedBillingDataResponse,
  GQL_CALCULATE_BILLING_DATA,
  GQL_PLAN_INFO,
  PlanInfoResult,
} from "@/lib/queries/billing.ts";
import {PlanLimits} from "@/lib/types/accounts.ts";
import {AddedValue, BillingPeriod, CalculatedBillingData, Plan} from "@/lib/types/billing.ts";
import {CheckFrequency, MonitoringFrequencyName} from "@/lib/types/devices.ts";
import {MonitoringGroupLevel, MonitoringGroupLevelName} from "@/lib/types/monitoring.ts";
import {PURCHASE_ITEM_NAMES} from "@/pages/billing/utils.ts";

import css from "./Quote.module.css";

export interface QuoteProps {
  isCustomer: boolean;
  isUpgrade: boolean;
  isAnnual: boolean;
  plan?: Plan;
  addedValues: AddedValue[];
  calculatedData: CalculatedBillingData;
}

export const Quote = ({isCustomer, isUpgrade, isAnnual, plan, addedValues, calculatedData}: QuoteProps) => {
  const {user, brand} = useCurrentUserContext();
  const {data: planData, loading} = useQuery<PlanInfoResult>(GQL_PLAN_INFO, {
    variables: {
      plan: plan,
    },
  });
  const {data: monthlyCalcData} = useQuery<CalculatedBillingDataResponse>(GQL_CALCULATE_BILLING_DATA, {
    variables: {
      billingPeriod: BillingPeriod.MONTH,
      plan,
      addedItems: addedValues,
      skipApplicabilityCheck: true,
    },
  });
  return (
    <div>
      <div className="row">
        <div className="col-3">
          <img className="img-fluid" src={logo} alt={brand.name} />
        </div>
        <div className="col-9 text-end">
          <small className="text-gray-500">
            <FaPhone className="me-2" />
            {brand.phone}
            <FaRegEnvelope className="ms-4 me-2" />
            {brand.email}
          </small>
        </div>
      </div>
      <hr />
      <h1 className="mb-4 fw-bolder fs-2">Purchase Quote</h1>
      <div className="row mb-5">
        <div className="col-6">
          <h2 className="fs-5 fw-bold">Customer Name</h2>
          <small className="lh-sm text-muted">
            {user?.account.name}
            <br />
            Subscription Date:{" "}
            {dateOnly(
              formatISO(user?.account?.converted_at || new Date()),
              user!.current_date_format,
              user!.account!.timezone
            )}
          </small>
        </div>
        <div className="col-6 ps-4" style={{borderLeft: "1px solid #C4C4C4"}}>
          <h2 className="fs-5 fw-bold">{brand.name} LLC</h2>
          <small
            className="lh-sm text-muted"
            dangerouslySetInnerHTML={{
              __html: linebreaksbr(brand.address),
            }}
          ></small>
        </div>
      </div>
      <div className="row mt-4 mb-2">
        <div className={isCustomer ? "col-8" : "col-6"}>
          <strong>Purchase Description</strong>
        </div>
        {!isCustomer && (
          <div className="col-2">
            <strong>Included</strong>
          </div>
        )}
        <div className="col-2">
          <strong>Added</strong>
        </div>
        <div className="col-2">
          <strong>Cost</strong>
        </div>
      </div>
      {(!isCustomer || isUpgrade) && (
        <div className="row mt-4">
          <div className="col-10">{calculatedData.base_plan}</div>
          <div className="col-2">{shortCurrency(calculatedData.base_price_subtotal, 2)}</div>
        </div>
      )}
      {!loading &&
        calculatedData &&
        Object.entries<string>(PURCHASE_ITEM_NAMES).map(([limitField, limitName]) => {
          const included = planData?.planInfo?.limits[limitField as keyof PlanLimits]?.included || 0;
          const added = addedValues.find((el) => el.limit_field === limitField)?.amount || 0;
          const unitsPrices = (
            (monthlyCalcData?.calculateBillingData as CalculatedBillingData)?.units_prices || []
          ).find((el) => el.name === limitField);
          const barePrice = (unitsPrices?.price || 0) * added;
          if ((isCustomer && !added) || (!isCustomer && !included && !added) || !limitName) {
            return false;
          }
          let includedVerbose: string;
          if (limitField === "limit_monitoring_group_level") {
            includedVerbose = MonitoringGroupLevelName[included as MonitoringGroupLevel];
          } else if (limitField === "limit_check_frequency") {
            includedVerbose = MonitoringFrequencyName[included as CheckFrequency];
          } else {
            includedVerbose = humanFriendlyInt(included as number);
          }
          return (
            <div key={limitField}>
              <hr className="purchase-quote-added-value-separator" />
              <div className="row">
                <div className={isCustomer ? "col-8" : "col-6 ps-5"}>{plural(limitName)}</div>
                {!isCustomer && <div className="col-2">{includedVerbose}</div>}
                <div className="col-2">{added ? "+" + added : ""}</div>
                <div className="col-2">{barePrice ? shortCurrency(barePrice, 2) : ""}</div>
              </div>
            </div>
          );
        })}

      <div className={classnames(css.Totals, "pb-4 mt-4")}>
        <div className="row mt-4 align-items-baseline">
          <div className="col-9 text-end">Subtotal</div>
          <div className="col-3 text-start">
            {shortCurrency(
              isCustomer && !isUpgrade ? calculatedData.added_cost_full : calculatedData.monthly_subtotal,
              2
            )}
          </div>
        </div>
        {calculatedData.discounts.map((el, idx) => {
          if (!el.value) {
            return false;
          }
          let suffix = "";
          if (isAnnual && el.percent === 0 && el.title.toLowerCase().startsWith("promo")) {
            suffix = "(annual)";
          }
          return (
            <div className="row mt-2" key={"discount-" + idx}>
              <div className="col-9 text-end">{el.title}</div>
              <div className="col-3 text-start">
                <strong className="fw-bold pulse-color-green-500">{shortCurrency(el.value * -1, 2)}</strong>
                {suffix ? <span className="font-14"> {suffix}</span> : null}
              </div>
            </div>
          );
        })}
        <div className="row mt-3 align-items-baseline">
          <div className="col-9 text-end">
            <strong>Grand Total Per Month</strong>
          </div>
          <div className="col-3 text-start fs-4 fw-semibold">
            {shortCurrency(isCustomer && !isUpgrade ? calculatedData.added_cost : calculatedData.price_monthly, 2)}
          </div>
        </div>
        {isAnnual && (
          <div className="row mt-1 align-items-baseline">
            <div className="col-9 text-end">
              <strong>Billed Annually</strong>
            </div>
            <div className="col-3 text-start fs-4 fw-semibold">
              {shortCurrency(
                isCustomer && !isUpgrade ? calculatedData.added_cost * 12 : calculatedData.price_monthly * 12,
                2
              )}
            </div>
          </div>
        )}
      </div>
      <div className="text-center mt-3 d-p-none">
        <button type="button" className="btn btn-link text-decoration-none" onClick={() => window.print()}>
          <ImPrinter className="me-2" />
          <span className="text-black">Print Quote</span>
        </button>
      </div>
    </div>
  );
};
